import { useScrollAnimationSection } from '../../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';

interface TottoriAnxiety {
  icon: string;
  emoji: string;
  title: string;
  desc: string;
  linkText: string;
  articleId: number;
  bgColor: string;
  iconColor: string;
  accentColor: string;
  image: string;
}

const tottoriAnxieties: TottoriAnxiety[] = [
  {
    icon: 'ri-temp-cold-line',
    emoji: '❄️',
    title: '冬の寒さ・湿気に強い家にしたい',
    desc: '山陰の冬は厳しく、湿気も多い。鳥取に適した断熱性能・気密性能の基準と、山陰の気候に合った住宅選びのポイントを解説します。',
    linkText: '鳥取の冬に強い家の作り方を見る（断熱・気密の基準）',
    articleId: 60,
    bgColor: '#EDF5F3',
    iconColor: '#4A8A7A',
    accentColor: '#4A8A7A',
    image: 'https://public.readdy.ai/ai/img_res/83992817275abb2e3d438b9e51d84aef.jpg',
  },
  {
    icon: 'ri-earthquake-line',
    emoji: '🏔️',
    title: '地盤・地震・雪荷重が心配',
    desc: '鳥取は活断層が近く、積雪地域も含まれます。地盤調査の重要性・耐震等級の選び方・積雪対応の屋根仕様まで、安全な家を建てるための確認事項を紹介。',
    linkText: '地震・積雪に強い家の条件を見る（地震・耐震ガイド）',
    articleId: 61,
    bgColor: '#F5F0EA',
    iconColor: '#8A6040',
    accentColor: '#8A6040',
    image: 'https://readdy.ai/api/search-image?query=A%20professional%20architectural%20foundation%20inspection%20scene%20with%20soil%20boring%20test%20equipment%2C%20geological%20survey%20diagram%20of%20Japanese%20residential%20land%2C%20safety%20and%20durability%20concept%20for%20house%20building%2C%20neutral%20beige%20and%20earth%20tones%2C%20technical%20and%20trustworthy%20atmosphere%2C%20no%20people%2C%20clear%20daytime%2C%20Tottori%20prefecture%20suburban%20setting&width=600&height=360&seq=anxiety002&orientation=landscape',
  },
  {
    icon: 'ri-map-pin-2-line',
    emoji: '📍',
    title: '鳥取の土地選びの基準が分からない',
    desc: '鳥取市・米子市・倉吉市、それぞれのエリア特性・地価相場・利便性の違いを徹底解説。失敗しない土地選びのチェックリストも公開しています。',
    linkText: 'エリア別の土地選びポイントを見る（鳥取市・米子市・倉吉市）',
    articleId: 25,
    bgColor: '#EEF0F5',
    iconColor: '#4A5A8A',
    accentColor: '#4A5A8A',
    image: 'https://readdy.ai/api/search-image?query=An%20aerial%20view%20of%20Tottori%20city%20Japan%20residential%20neighborhood%20with%20mountains%20in%20background%2C%20quiet%20suburban%20streets%20with%20Japanese%20houses%2C%20green%20hills%20and%20San-in%20coastline%20visible%20in%20distance%2C%20warm%20golden%20hour%20light%2C%20peaceful%20and%20livable%20community%2C%20real%20estate%20land%20selection%20concept%2C%20muted%20natural%20tones&width=600&height=360&seq=anxiety003&orientation=landscape',
  },
];

export default function AnxietySection() {
  const sectionRef = useScrollAnimationSection();
  const navigate = useNavigate();

  return (
    <section
      id="anxiety"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FAFAF8]"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14 fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8F0E6] text-[#5A7A52] text-xs font-semibold tracking-wide mb-4">
            <i className="ri-map-pin-line" aria-hidden="true" />
            鳥取ならではの悩み
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] leading-tight">
            鳥取の家づくりで<br className="md:hidden" />よくある悩み、ありませんか？
          </h2>
          <p className="mt-4 text-[#6B6B6B] text-base leading-relaxed max-w-lg mx-auto">
            全国共通の情報では解決できない、鳥取・山陰エリア特有の課題があります。<br className="hidden md:block" />
            このサイトでは、地域に根ざした具体的な回答を提供しています。
          </p>
        </div>

        {/* 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tottoriAnxieties.map((item, index) => (
            <div
              key={item.title}
              className="fade-in-up group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="w-full h-44 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  width="600"
                  height="360"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-5">
                {/* Icon + Title */}
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: item.bgColor }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.iconColor }} aria-hidden="true" />
                  </div>
                  <h3 className="text-[#2C2C2C] text-sm font-bold leading-snug pt-1">
                    {item.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-[#6B6B6B] text-xs leading-relaxed mb-4">
                  {item.desc}
                </p>

                {/* Link */}
                <button
                  type="button"
                  onClick={() => navigate(`/articles/${item.articleId}`)}
                  className="inline-flex items-start gap-1.5 text-xs font-semibold cursor-pointer hover:gap-2.5 transition-all duration-200 text-left"
                  style={{ color: item.accentColor }}
                >
                  <i className="ri-arrow-right-circle-line text-base flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="leading-snug">{item.linkText}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="fade-in-up mt-10 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#8FA882]/10 border border-[#8FA882]/30">
            <i className="ri-map-2-line text-[#8FA882] text-lg" aria-hidden="true" />
            <p className="text-[#5A7A52] text-sm font-medium">
              鳥取市・米子市・倉吉市、それぞれの地域特性に対応した情報を掲載しています
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
